import { readEmbeddedImageRecord, type EmbeddedImageData } from './embeddedImageUtils';
import {
  readMultimodalAnalysisFromMetadata,
  type ParticleMultimodalAnalysis,
} from './ttqParticleMultimodalBias';

/** 颗粒原始素材图信息：优先给出裁剪信息，否则退化为整图 URL。 */
export type ParticleSourceImage = {
  crop?: EmbeddedImageData;
  fullSourceUrl?: string;
  /** 私有 OSS id：与详情页一致，用于签名后加载，避免过期直链卡在加载中 */
  ossId?: string;
  /** 已完成 asset 拉取（含无 oss_id 的情况），避免预填缓存短路签名解析 */
  assetResolved?: boolean;
  multimodalAnalysis?: ParticleMultimodalAnalysis | null;
};

const cache = new Map<string, ParticleSourceImage | null>();

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function buildParticleSourceImageFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): ParticleSourceImage | null {
  const crop = readEmbeddedImageRecord(metadata);
  const embedded = asRecord(metadata?.['relatedGraphEmbeddedImage']) || asRecord(metadata?.['embeddedImage']);
  const embeddedUrl = typeof embedded?.['sourceImageUrl'] === 'string'
    ? String(embedded['sourceImageUrl']).trim()
    : '';
  const fullSourceUrl = embeddedUrl || crop?.sourceImageUrl || '';
  const multimodalAnalysis = readMultimodalAnalysisFromMetadata(metadata);
  if (!crop && !fullSourceUrl && !multimodalAnalysis) return null;
  return {
    crop: crop ?? undefined,
    fullSourceUrl: fullSourceUrl || undefined,
    multimodalAnalysis,
  };
}

export function getParticleSourceImageCache(particleId: string): ParticleSourceImage | null | undefined {
  if (!cache.has(particleId)) return undefined;
  return cache.get(particleId) ?? null;
}

export function setParticleSourceImageCache(particleId: string, record: ParticleSourceImage | null): void {
  cache.set(particleId, record);
}

export function seedParticleSourceImageCache(particleId: string, metadata: unknown): void {
  if (cache.has(particleId)) return;
  const built = buildParticleSourceImageFromMetadata(asRecord(metadata));
  if (built) cache.set(particleId, built);
}

export function invalidateParticleSourceImageCache(particleId: string): void {
  cache.delete(particleId);
}
